import { z } from "zod";

export const TokenUsageSchema = z.object({
  // Number of input tokens consumed by this completion.
  input_tokens: z.number().int().nullable().default(null),
  // Number of output tokens produced by this completion.
  output_tokens: z.number().int().nullable().default(null),
  // Number of reasoning tokens consumed by this completion.
  reasoning_tokens: z.number().int().nullable().default(null),
  // Number of cached tokens used by this completion.
  cached_tokens: z.number().int().nullable().default(null),
  // Number of oldest non-system messages the backend had to drop from the
  // request before sending it to the LLM to fit the model's context window.
  truncated_messages: z.number().int().nullable().default(null),
  // Estimated prompt token count that will replay on the next turn (system
  // message + trimmed conversation history + tool schemas), excluding any new
  // user input. Unlike `input_tokens`, does not scale with the number of tool
  // iterations within a turn. See API-side ADR 036.
  history_tokens: z.number().int().nullable().default(null),
  // Usable input window after the bound-tool schema reserve.
  context_budget_tokens: z.number().int().nullable().default(null),
  // Token threshold at which compaction triggers. Null when compaction is disabled.
  compaction_trigger_tokens: z.number().int().nullable().default(null),
  // History size measured on the same basis as the compaction trigger comparison.
  // Null when it cannot be measured.
  trigger_history_tokens: z.number().int().nullable().default(null),
});
export type TokenUsage = z.infer<typeof TokenUsageSchema>;

/**
 * Represents a chat completion request record in JSON format.
 */
export const ChatRecordJsonSchema = z.object({
  // UUIDv4 identifier. Guaranteed to uniquely identify a specific chat
  // completion request.
  request_id: z.string().uuid().optional(),
  // The request URL.
  // So we can tell whether a request was made against our sandbox or prod API.
  // e.g. "https://corteva-chat-api.dataplatform.research.vylor.com/chat/completions"
  request_url: z.string(),
  // ISO-8601 DateTime string in UTC corresponding to the time this request was
  // made.
  // e.g. "2026-02-24T02:13:07Z"
  requested_at: z.string().datetime().optional(),
  // The HTTP status code of this chat completion request's response.
  // https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Status
  // e.g. `200`
  status_code: z.number().int(),
  // The text of this chat completion request's response's status message
  // as returned by the HTTP server.
  // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/statusText
  // e.g. "OK"
  status_text: z.string(),
  // The last user message in the message history.
  // e.g. "What is the capital of Canada?"
  last_user_message: z.string(),
  // The model requested by this chat completion request.
  // e.g. "claude-opus-4-6@default"
  model: z.string(),
  // Chat messages as a JSON string.
  messages: z.string().nullable().default(null),
  // The username of the user who made the request.
  // e.g. "ben.pyrik@vylor.com"
  requested_by: z.string().nullable().default(null),
  // The duration of the chat completion request in milliseconds.
  // Measured from the time it was sent (`xhr.send()`) to the time it ended
  // (`xhr.readyState === 4`).
  // e.g. `567`
  request_duration: z.number().nullable().default(null),
  // The number of characters in the response text. Probably `xhr.responseText.length`.
  // e.g. `203`
  response_length: z.number().nullable().default(null),
  // Optional error message.
  // e.g. "request failed for unknown reasons"
  error_message: z.string().nullable().default(null),
  // UUIDv4 identifier. Uniquely identifies a specific chat thread this request
  // relates to.
  chat_thread_id: z.string().uuid(),
  // The UUIDv4 identifier for the last message generated by the chat completions
  // request. This allows us to guess at the messages sent in the request (by
  // assuming the messages were: [first message, last user message before
  // a message with `last_response_message_id`])
  // Will be `null` if the request failed and no response message was generated.
  last_response_message_id: z.string().uuid().nullable().default(null),
  // A textual form of the response messages generated by this chat completions
  // request. Tool calls and regular assistant response messages may be squashed
  //  together into a string to create this 'message'.
  // Will be `null` if the request failed and no response message was generated.
  // e.g. "The capital of Canada is **Ottawa**."
  last_response_message: z.string().nullable().default(null),
  // Whether the search tool was requested or not.
  enable_search: z.boolean().nullable().default(null),
  // Number of input, output, reasoning, and cached tokens used in this request.
  // Will be `null` if the request failed and no tokens were consumed.
  token_usage: TokenUsageSchema.nullable().default(null),
  // The name of the persona used in this request.
  // Will be `null` if no persona was used.
  // e.g. "Biotech Data Agent"
  persona_name: z.string().nullable().default(null),
  // The ID of the persona used in this request.
  // Will be `null` if no persona was used.
  // e.g. "31"
  persona_id: z.string().nullable().default(null),
  // MCP server configuration as a JSON string, or null if no MCP servers were
  // used.
  mcp_config: z.string().nullable().default(null),
});

export type ChatRecordJson = z.infer<typeof ChatRecordJsonSchema>;
